import { useCallback, useEffect, useRef, useState } from "react";
import { api, unwrap } from "@/api/client";

/**
 * Packing video (Plan §6.9): the desk's camera, recorded through a canvas so
 * every frame carries the order number, the time and the warehouse. One
 * recording ("segment") per parcel; the caller cuts it when the parcel closes
 * and uploads it to that order.
 */

const FPS = 15;
const WIDTH = 960;
const HEIGHT = 540;

export type Segment = { blob: Blob; seconds: number };

function pickMime(): string {
  for (const m of ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"]) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

function stamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export type Recorder = {
  /** camera live and recording possible */
  ready: boolean;
  error: string | null;
  recording: boolean;
  /** the live camera, for a small preview */
  stream: MediaStream | null;
  /** start a new segment (ends any running one, discarding it); the order when known */
  start: (label: string, orderId?: string) => void;
  /** change the text burnt into the frames */
  setLabel: (label: string) => void;
  /** end the segment and hand it over */
  stop: () => Promise<Segment | null>;
  /** end the segment of a parcel that closed: the browser hands it over to
   * upload; the recorder agent keeps it and tells Zerpid itself (null) */
  finish: (orderId: string, externalId: string) => Promise<Segment | null>;
  /** end the segment and drop it */
  discard: () => void;
};

export function usePackingRecorder(enabled: boolean, place: string): Recorder {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const label = useRef("");
  const canvas = useRef<HTMLCanvasElement | null>(null);
  // The running segment; each keeps its own data, so a new one can start
  // while the last is still being closed.
  const rec = useRef<{ r: MediaRecorder; chunks: Blob[]; startedAt: number } | null>(null);

  // Camera and the drawing loop live while recording is on for the desk.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: number | undefined;
    let cam: MediaStream | null = null;
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    const media = navigator.mediaDevices
      ? navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
      : Promise.reject(new Error("camera_unavailable"));
    media
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        cam = s;
        video.srcObject = s;
        void video.play();
        const c = document.createElement("canvas");
        c.width = WIDTH;
        c.height = HEIGHT;
        canvas.current = c;
        const g = c.getContext("2d")!;
        timer = window.setInterval(() => {
          g.drawImage(video, 0, 0, WIDTH, HEIGHT);
          g.fillStyle = "rgba(0,0,0,0.6)";
          g.fillRect(0, HEIGHT - 44, WIDTH, 44);
          g.fillStyle = "white";
          g.font = "bold 22px sans-serif";
          g.fillText([label.current, stamp(new Date()), place].filter(Boolean).join("  ·  "), 14, HEIGHT - 15);
        }, 1000 / FPS);
        setStream(s);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      if (rec.current && rec.current.r.state !== "inactive") rec.current.r.stop();
      rec.current = null;
      cam?.getTracks().forEach((t) => t.stop());
      canvas.current = null;
      setStream(null);
      setRecording(false);
    };
  }, [enabled, place]);

  const end = useCallback((): Promise<Segment | null> => {
    const seg = rec.current;
    rec.current = null;
    setRecording(false);
    if (!seg || seg.r.state === "inactive") return Promise.resolve(null);
    return new Promise((resolve) => {
      seg.r.onstop = () => {
        const blob = new Blob(seg.chunks, { type: seg.r.mimeType.split(";")[0] || "video/webm" });
        resolve(blob.size > 0 ? { blob, seconds: Math.round((Date.now() - seg.startedAt) / 1000) } : null);
      };
      seg.r.stop();
    });
  }, []);

  const start = useCallback(
    (text: string) => {
      label.current = text;
      const c = canvas.current;
      if (!c) return;
      if (rec.current) void end();
      const mime = pickMime();
      const r = new MediaRecorder(c.captureStream(FPS), { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 1_000_000 });
      const seg = { r, chunks: [] as Blob[], startedAt: Date.now() };
      r.ondataavailable = (e) => {
        if (e.data.size > 0) seg.chunks.push(e.data);
      };
      r.start(1000);
      rec.current = seg;
      setRecording(true);
    },
    [end],
  );

  const setLabel = useCallback((text: string) => {
    label.current = text;
  }, []);
  const discard = useCallback(() => void end(), [end]);
  const finish = useCallback(() => end(), [end]);
  return { ready: stream !== null, error, recording, stream, start, setLabel, stop: end, finish, discard };
}

/** Sends a parcel's recording to its order. */
export async function uploadVideo(orderId: string, seg: Segment): Promise<void> {
  await unwrap(
    api.POST("/v1/packing/orders/{orderID}/video", {
      params: { path: { orderID: orderId }, query: { seconds: seg.seconds } },
      body: seg.blob as unknown as string,
      bodySerializer: (b: unknown) => b as Blob,
      headers: { "Content-Type": seg.blob.type || "video/webm" },
    }),
  );
}

/** Sends an unboxing recording to its aftersales case. */
export async function uploadCaseVideo(caseId: string, seg: Segment): Promise<void> {
  await unwrap(
    api.POST("/v1/aftersales/{caseID}/video", {
      params: { path: { caseID: caseId }, query: { seconds: seg.seconds } },
      body: seg.blob as unknown as string,
      bodySerializer: (b: unknown) => b as Blob,
      headers: { "Content-Type": seg.blob.type || "video/webm" },
    }),
  );
}
